import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, Skeleton } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import { useDispatch } from 'react-redux';
import { AppThunkDispatch } from 'src/app-types';
import { AvailableIcons, Icon, Tooltip } from 'src/components-dummy';
import { useAppSelector } from 'src/hooks';
import { ProductsMetadataState } from 'src/reducers';
import { kpiService } from 'src/services/src/service/analytics/kpi.service';
import { metadataActions } from '../../actions';
import { TimeRangeEnum } from '../FilterSection/TimeRangeSelector/types';
import { DeviceFilterEnum } from '../FilterSection/DeviceSelector/types';
import GenericSelect from '../GenericSelect/GenericSelect';
import { Option } from '../GenericSelect/types';
import PieChart from '../PieChart/PieChart';
import { getKPILabel, KPIMetricsEnum } from '../OvertimeAnalysis/types';
import { formatMetricLabel } from '../../utils/utils';

interface ProductMetricDistributionProps {
  timeRange: TimeRangeEnum;
  device: DeviceFilterEnum;
  shopId: number;
}

interface ProductMetricValue {
  product: string;
  value: number;
}

const PRODUCT_ORDER = [
  'All',
  'Discovery Button',
  'Similar Items',
  'Shop The Look',
  'Bought Together',
  'Personalization',
  'Collections',
  'Text Search',
  'Camera',
  'Website',
];

const LABEL_OVERRIDES: Record<string, string> = {
  All: 'All Syte Products',
  'Discovery Button': 'Discovery Icon',
  'Similar Items': 'Similar Items Carousel',
  'Shop The Look': 'Shop the Look Carousel',
  Camera: 'Camera Search',
};

const normalizedKey = (value: string) => value.trim().toLowerCase();
const orderIndex = new Map(PRODUCT_ORDER.map((name, idx) => [normalizedKey(name), idx]));

const sortProducts = (products: string[]) =>
  [...products].sort((a, b) => {
    const aKey = normalizedKey(a);
    const bKey = normalizedKey(b);
    const aRank = orderIndex.get(aKey);
    const bRank = orderIndex.get(bKey);
    if (aRank != null && bRank != null) return aRank - bRank;
    if (aRank != null) return -1;
    if (bRank != null) return 1;
    return a.localeCompare(b);
  });

const PIE_COLORS = [
  '#005CCC',
  '#2E7CF5',
  '#1FB6FF',
  '#4CD7D0',
  '#64B5F6',
  '#81C784',
  '#FFD54F',
  '#FF8A65',
  '#A1887F',
  '#90A4AE',
];

export const ProductMetricDistribution = ({
  timeRange,
  device,
  shopId,
}: ProductMetricDistributionProps): JSX.Element => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [selectedMetric, setSelectedMetric] = useState<KPIMetricsEnum>(KPIMetricsEnum.SyteRevenue);
  const [productValues, setProductValues] = useState<ProductMetricValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const productsMetadata = (useAppSelector(
    state => state.dataInsights.productsMetadata
  ) as ProductsMetadataState) || {
    data: [],
    loading: false,
    error: null,
  };
  const isMetadataLoading = productsMetadata.loading;

  useEffect(() => {
    if (!productsMetadata.data && !productsMetadata.loading) {
      dispatch(metadataActions.getProductsMetadata({ shopId }));
    }
  }, [dispatch, productsMetadata.data, productsMetadata.loading, shopId]);

  const productList = useMemo(() => {
    const list = productsMetadata.data || [];
    const filtered = list.filter(product => !['All', 'All Syte Products'].includes(product.trim()));
    return sortProducts(filtered);
  }, [productsMetadata.data]);

  const allowedMetrics: KPIMetricsEnum[] = [
    KPIMetricsEnum.SyteRevenue,
    KPIMetricsEnum.CTR,
    KPIMetricsEnum.CVR,
    KPIMetricsEnum.AOV,
    KPIMetricsEnum.ARPU,
    'impressions' as KPIMetricsEnum,
  ];
  const metricLabelOverrides: Partial<Record<KPIMetricsEnum, string>> = {
    ['impressions' as KPIMetricsEnum]: 'Impressions',
  };
  const kpiMetricOptions: Option<KPIMetricsEnum>[] = allowedMetrics.map(val => ({
    value: val,
    label: metricLabelOverrides[val] || getKPILabel(val) || formatMetricLabel(val),
  }));

  useEffect(() => {
    if (!productList.length) {
      setProductValues([]);
      return;
    }

    let isActive = true;
    setLoading(true);
    setError(null);

    const fetchValues = async () => {
      try {
        const results = await Promise.all(
          productList.map(async productName => {
            const response = await kpiService.getKpiMetric({
              shopId,
              kpiType: selectedMetric,
              timeRange,
              product: productName,
              device,
            });
            return { product: productName, value: Number(response.value || 0) };
          })
        );

        if (isActive) {
          setProductValues(results);
          setLoading(false);
        }
      } catch (err: any) {
        if (isActive) {
          setError(err?.message || 'Error fetching product breakdown');
          setLoading(false);
        }
      }
    };

    fetchValues();

    return () => {
      isActive = false;
    };
  }, [productList, selectedMetric, timeRange, device, shopId]);

  const chartData = useMemo<ChartData<'doughnut'> | null>(() => {
    if (!productValues.length) return null;
    const labels = productValues.map(item => LABEL_OVERRIDES[item.product] || item.product);
    const values = productValues.map(item => item.value);
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: values.map((_, idx) => PIE_COLORS[idx % PIE_COLORS.length]),
          borderWidth: 0,
          hoverOffset: 10,
        },
      ],
    };
  }, [productValues]);

  const pieOptions = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '40%',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => {
              const dataset = (ctx.dataset.data || []) as Array<number | string>;
              const total = dataset.reduce<number>((sum, v) => sum + Number(v), 0);
              const value = Number(ctx.raw || 0);
              const totalValue = Number(total);
              const percent = totalValue > 0 ? (value / totalValue) * 100 : 0;
              return `${ctx.label}: ${percent.toFixed(1)}%`;
            },
          },
        },
      },
      layout: {
        padding: {
          top: 20,
          bottom: 20,
          left: 20,
          right: 20,
        },
      },
    }),
    []
  );

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Box display='flex' alignItems='center'>
          <Typography variant='h6' sx={{ fontWeight: 700, width: 'fit-content' }}>
            Product Breakdown
          </Typography>
          <Tooltip value='Distribution of the selected KPI across Syte products. Percentages are calculated from the visible product values, not from total website revenue.'>
            <IconButton size='small' sx={{ ml: 1, color: '#6E6E71' }}>
              <Icon name={AvailableIcons.InformationV3} />
            </IconButton>
          </Tooltip>
        </Box>
        <GenericSelect<KPIMetricsEnum>
          value={selectedMetric}
          onChange={event => setSelectedMetric(event.target.value as KPIMetricsEnum)}
          icon={null}
          placeholder='Select metric...'
          options={kpiMetricOptions}
        />
      </Box>

      {loading || isMetadataLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            minHeight: 320,
            mt: 2,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: 220,
              height: 220,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Skeleton variant='circular' width={220} height={220} sx={{ position: 'absolute' }} />
            <Skeleton
              variant='circular'
              width={90}
              height={90}
              sx={{
                position: 'absolute',
                backgroundColor: 'background.paper',
              }}
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.8,
              width: '100%',
              maxWidth: 250,
              pl: 2,
            }}
          >
            {[1, 2, 3].map((_, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant='rectangular' width={8} height={8} />
                <Skeleton variant='text' width={180} height={24} />
              </Box>
            ))}
          </Box>
        </Box>
      ) : error ? (
        <Box color='error.main' textAlign='center' py={4}>
          <Typography color='error'>{error}</Typography>
        </Box>
      ) : chartData ? (
        <PieChart data={chartData} options={pieOptions} />
      ) : (
        <Box textAlign='center' py={6}>
          <Typography color='text.secondary'>No data available.</Typography>
        </Box>
      )}
    </Box>
  );
};
